/**
 * AdminPlausiSchwellwerteSection — Plausibilitäts-Schwellwerte für den
 * Forderungsmanager (Issue #268).
 *
 * Andreas kann hier die sechs Schwellwerte für Schadentag ↔ Mietbeginn,
 * Mietende ↔ Rechnungsdatum und Verjährung kalibrieren. Die Werte
 * werden in `app_settings` persistiert; die Plausi-Prüfung (Issue #267
 * bzw. Frontend-Fallback aus #269) liest sie zur Laufzeit.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw, Save, Scale, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  DEFAULT_PLAUSI_THRESHOLDS,
  PLAUSI_SETTING_KEYS,
  PLAUSI_THRESHOLD_LABELS,
  type PlausiThresholdKey,
  type PlausiThresholds,
  getPlausiThresholds,
  savePlausiThresholds,
  validatePlausiThresholds,
} from '@/lib/plausi-settings';

const GRUPPEN_TITEL: Record<'schadentag' | 'abrechnung' | 'verjaehrung', string> = {
  schadentag: 'Schadentag ↔ Mietbeginn',
  abrechnung: 'Mietende ↔ Rechnungsdatum',
  verjaehrung: 'Verjährung',
};

const GRUPPEN_ERKLAERUNG: Record<'schadentag' | 'abrechnung' | 'verjaehrung', string> = {
  schadentag:
    'Wie viele Tage dürfen zwischen Schaden und Mietbeginn liegen? Je näher, desto plausibler.',
  abrechnung:
    'Wie lange nach Mietende darf die Rechnung gestellt worden sein, bevor die Akte auffällt?',
  verjaehrung:
    'Wann schlägt die Verjährungsprüfung an? Weniger Tage Restlaufzeit = dringender.',
};

export default function AdminPlausiSchwellwerteSection() {
  const [values, setValues] = useState<PlausiThresholds>(DEFAULT_PLAUSI_THRESHOLDS);
  const [originalValues, setOriginalValues] = useState<PlausiThresholds>(DEFAULT_PLAUSI_THRESHOLDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<PlausiThresholdKey, string>>>({});

  const load = async () => {
    setLoading(true);
    try {
      const loaded = await getPlausiThresholds();
      setValues(loaded);
      setOriginalValues(loaded);
      setErrors({});
    } catch (e) {
      console.error(e);
      toast.error('Schwellwerte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (key: PlausiThresholdKey, raw: string) => {
    const next = { ...values, [key]: raw === '' ? Number.NaN : Number(raw) };
    setValues(next);
    setErrors(validatePlausiThresholds(next).errors);
  };

  const handleReset = () => {
    setValues(DEFAULT_PLAUSI_THRESHOLDS);
    setErrors({});
  };

  const handleSave = async () => {
    const validation = validatePlausiThresholds(values);
    setErrors(validation.errors);
    if (!validation.ok) {
      toast.error('Bitte zuerst die markierten Fehler beheben');
      return;
    }
    setSaving(true);
    try {
      await savePlausiThresholds(values);
      setOriginalValues(values);
      toast.success('Schwellwerte gespeichert');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unbekannter Fehler';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const dirty = PLAUSI_SETTING_KEYS.some((k) => values[k] !== originalValues[k]);
  const hasErrors = Object.keys(errors).length > 0;

  const gruppen: Array<'schadentag' | 'abrechnung' | 'verjaehrung'> = [
    'schadentag',
    'abrechnung',
    'verjaehrung',
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
            <Scale className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">
              Forderungsmanager — Plausibilitäts-Schwellwerte
            </h2>
            <p className="text-xs text-gray-400">
              Ampel-Grenzen für die vier Plausi-Checks (Schadentag, Abrechnung, Verjährung).
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3">
          <ShieldCheck className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <p>
            Werte gelten global. Validierung: <strong>Gelb &gt; Grün</strong> bei Schadentag/Abrechnung,{' '}
            <strong>Rot &lt; Gelb</strong> bei Verjährung (weniger Restfrist = dringender). Inkonsistente Werte werden nicht gespeichert.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm flex items-center justify-center text-xs text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Lade Schwellwerte…
        </div>
      ) : (
        <>
          {gruppen.map((gruppe) => {
            const felder = PLAUSI_THRESHOLD_LABELS.filter((l) => l.gruppe === gruppe);
            return (
              <div
                key={gruppe}
                className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
              >
                <h3 className="text-sm font-bold text-gray-900">
                  {GRUPPEN_TITEL[gruppe]}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5 mb-4">
                  {GRUPPEN_ERKLAERUNG[gruppe]}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {felder.map((f) => {
                    const err = errors[f.key];
                    return (
                      <div key={f.key}>
                        <label
                          htmlFor={f.key}
                          className="text-xs font-medium text-gray-600"
                        >
                          {f.label}
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            id={f.key}
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            aria-label={f.label}
                            data-testid={`plausi-input-${f.key}`}
                            value={
                              Number.isNaN(values[f.key]) ? '' : values[f.key]
                            }
                            onChange={(e) => handleChange(f.key, e.target.value)}
                            className={`w-28 px-3 py-2 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 ${
                              err
                                ? 'border-red-400 focus:ring-red-400/30'
                                : 'border-gray-200 focus:ring-[#9B1C1C]/20'
                            }`}
                          />
                          <span className="text-[11px] text-gray-400">Tage</span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1">
                          {f.beschreibung}
                        </p>
                        {err && (
                          <p
                            role="alert"
                            data-testid={`plausi-error-${f.key}`}
                            className="text-[11px] text-red-600 mt-1 flex items-center gap-1"
                          >
                            <AlertCircle className="w-3 h-3" />
                            {err}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handleReset}
              disabled={saving || loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Auf Standard zurücksetzen
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || hasErrors || !dirty}
              data-testid="plausi-save-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-medium hover:bg-[#7A1616] transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
